import React from 'react';
import styled, { css } from 'styled-components';
import { Link, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, firstImageProperty } from '@shapeable/utils';
import { filter, get } from 'lodash';
import {
  EntityCardProps,
  EntityCardDefaultProps,
  EntityCard,
  EntityImage,
  DownloadButton,
  useEntityPathDeriver,
} from '@shapeable/ui';

const cls = classNames('link-card-town-team');

// -------- Types -------->

// -------- Props -------->

export type LinkCardTownTeamProps = Omit<EntityCardProps, 'entity'> & { 
  entity?: Link;
};

export const LinkCardTownTeamDefaultProps: Omit<LinkCardTownTeamProps, 'entity'> = {
  ...EntityCardDefaultProps,
};

// -------- Child Component Props -------->

type ContainerProps = {};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    padding-top: ${theme.UNIT(5)};
    && {
      outline: none !important;
      box-shadow: none !important;

      &:hover {
        box-shadow: none !important;
      }

      .shp--entity-card__border,
      .shp--entity-card__container {
        border-radius: ${theme.UNIT(3)};
        img {
          border-radius: ${theme.UNIT(2)};
        }
      }
    }
  `,
});

const ImageStyles = breakpoints({
  base: css`
  
  `,
});

const DownloadButtonStyles = breakpoints({
  base: css`
    max-width: fit-content;
    border-radius: ${theme.UNIT(5)};
    ${theme.TRANSITION('box-shadow', 'scale')};

    &:hover {
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      scale: 1.01;
    }

    &:active {
      scale: 0.99;
    }
  `,
});

// -------- Components -------->

const My = {
  Container: styled(EntityCard)<ContainerProps>`${ContainerStyles}`,
  Image: styled(EntityImage)`${ImageStyles}`,
  DownloadButton: styled(DownloadButton)`${DownloadButtonStyles}`,
};

export const LinkCardTownTeam: Shapeable.FC<LinkCardTownTeamProps> = (props) => {
  const { className, entity, ...cardProps } = props;

  const image = firstImageProperty(
    entity,
    filter(['image', 'openGraph.image.thumbnails.card', 'banner.thumbnails', 'banner.image']),
  );

  const entityImageAugment: Link = {
    ...entity,
    url: entity?.file?.url?.replace(/upload\//, 'upload/fl_attachment/'),
    openGraph: {
      id: 'og-' + entity?.id,
      image,
    },
  };

  const fileUrl = get(entity, 'file.file.url');
  const href = fileUrl || entity?.url;

  return (
    <My.Container
      {...cardProps}
      className={cls.name(className)}
      entity={entityImageAugment}
      showExternalLink={false}
      showColorStrip={false}
      showTypeHeader={false}
      showDescription
      showDescriptionDivider={false}
      showImage={false}
      useLink={false}
      head={<My.Image entity={entityImageAugment} aspectRatio={{ base: 1 }} />}
      foot={
        href && 
          <My.DownloadButton 
            href={href} 
            iconSize={15}
            variant='slim'
            borderStyle='solid'
            shape='rounded'
            colors={{ fill: 'light', background: 'primary', text: 'light', border: 'primary' }}
            />
      }
    />
  );
};

LinkCardTownTeam.defaultProps = LinkCardTownTeamDefaultProps;
LinkCardTownTeam.cls = cls;
